import React from 'react';
import { useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { AlertTriangle, RefreshCcw, Home, Smartphone, AlertCircle } from 'lucide-react';
import { ensureLoggedIn } from '../services/liff';

const ErrorPage = () => {
  const location = useLocation();
  
  // Get error from URL params or location state
  const urlParams = new URLSearchParams(location.search);
  const errorFromUrl = urlParams.get('m');
  const error = errorFromUrl || location.state?.error || 'An unexpected error occurred';
  
  const handleRetryLogin = async () => {
    try {
      const ok = await ensureLoggedIn();
      if (ok) {
        window.location.replace('/me');
      }
      // If not ok, ensureLoggedIn already redirected to LINE login
    } catch (e) {
      alert(String(e));
    }
  };

  const handleGoHome = () => {
    window.location.replace('/');
  };

  // Determine if this is a configuration error vs runtime error
  const isConfigError = error.includes('Missing') && error.includes('from env');
  const isTokenError = error.includes('access_token') || error.includes('id_token');

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-red-50 to-orange-100">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center space-y-4">
          <div className="w-20 h-20 mx-auto bg-red-500 rounded-2xl flex items-center justify-center shadow-lg">
            {isConfigError ? (
              <AlertCircle className="w-10 h-10 text-white" />
            ) : (
              <AlertTriangle className="w-10 h-10 text-white" />
            )}
          </div>
          <h1 className="text-2xl font-bold text-slate-800">
            {isConfigError ? 'Configuration Error' : 'LINE Login Error'}
          </h1>
          <p className="text-slate-600">
            {isConfigError 
              ? 'There is a problem with the app configuration'
              : isTokenError
                ? 'Authentication tokens are missing or invalid'
                : 'There was a problem with your LINE login'
            }
          </p>
        </div>

        <Card className="shadow-xl border-0">
          <CardHeader>
            <CardTitle className="text-lg text-center flex items-center justify-center gap-2">
              <Smartphone className="w-5 h-5 text-red-500" />
              Error Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-700 font-medium mb-2">Error Message:</p>
              <p className="text-sm text-red-600 break-words font-mono">{error}</p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-700 font-medium mb-2">Possible Solutions:</p>
              <ul className="text-sm text-amber-600 space-y-1">
                {isConfigError ? (
                  <>
                    <li>• Check your .env file configuration</li>
                    <li>• Ensure all REACT_APP_ environment variables are set</li>
                    <li>• Verify LIFF ID and redirect URI are correct</li>
                    <li>• Rebuild the application after .env changes</li>
                  </>
                ) : isTokenError ? (
                  <>
                    <li>• Make sure you're accessing from LINE app</li>
                    <li>• Check LIFF app scopes in Developer Console</li>
                    <li>• Verify callback URL matches exactly</li>
                    <li>• Try logging out and logging in again</li>
                  </>
                ) : (
                  <>
                    <li>• Check your internet connection</li>
                    <li>• Make sure you're accessing this from LINE app</li>
                    <li>• Verify LINE LIFF configuration in Developer Console</li>
                    <li>• Try refreshing the page</li>
                  </>
                )}
              </ul>
            </div>

            <div className="space-y-3 pt-2">
              {!isConfigError && (
                <Button 
                  onClick={handleRetryLogin}
                  className="w-full bg-red-500 hover:bg-red-600 text-white"
                >
                  <RefreshCcw className="w-4 h-4 mr-2" />
                  Retry Login
                </Button>
              )}
              
              <Button 
                onClick={handleGoHome}
                variant="outline"
                className="w-full border-red-200 text-red-600 hover:bg-red-50"
              >
                <Home className="w-4 h-4 mr-2" />
                Go to Home
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="text-center text-xs text-slate-400 space-y-1">
          <p>If you continue to experience issues</p>
          <p>Please contact support or check your configuration</p>
        </div>
      </div>
    </div>
  );
};

export default ErrorPage;